import { REALTIME_SSE_EVENT, realtimeEventSchema } from '@mfos/shared';
import { type QueryClient, type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';
import { workspacePath } from '../workspace/workspace.api';
import { keysForEvent, resyncKeys } from './realtime-keys';

/** A stream closed by the server (e.g. expired session) is retried after this delay. */
const RECONNECT_DELAY_MS = 30_000;

function invalidate(client: QueryClient, keys: readonly QueryKey[]): void {
  for (const queryKey of keys) {
    void client.invalidateQueries({ queryKey });
  }
}

/**
 * One Server-Sent Events connection per open workspace. Each hint invalidates the affected
 * TanStack Query keys; EventSource reconnects after network errors by itself.
 */
export function RealtimeProvider({
  workspaceId,
  children,
}: {
  workspaceId: string;
  children: ReactNode;
}) {
  const client = useQueryClient();

  useEffect(() => {
    if (typeof EventSource === 'undefined') {
      return undefined;
    }
    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let hadError = false;
    let disposed = false;

    const onHint = (message: MessageEvent<unknown>) => {
      if (typeof message.data !== 'string') {
        return;
      }
      let raw: unknown;
      try {
        raw = JSON.parse(message.data);
      } catch {
        return;
      }
      const parsed = realtimeEventSchema.safeParse(raw);
      if (!parsed.success || parsed.data.workspaceId !== workspaceId) {
        return;
      }
      invalidate(client, keysForEvent(parsed.data));
    };

    const connect = () => {
      const current = new EventSource(workspacePath(workspaceId, 'events'), {
        withCredentials: true,
      });
      source = current;
      current.addEventListener(REALTIME_SSE_EVENT, onHint);
      current.addEventListener('open', () => {
        if (hadError) {
          hadError = false;
          invalidate(client, resyncKeys(workspaceId));
        }
      });
      current.addEventListener('error', () => {
        hadError = true;
        // CLOSED means the browser gave up (e.g. 401/403); CONNECTING retries natively.
        if (current.readyState === EventSource.CLOSED && !disposed) {
          current.close();
          reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      });
    };

    connect();
    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      source?.close();
    };
  }, [client, workspaceId]);

  return <>{children}</>;
}
